import { HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AIDRAggregations, AggregationsModel, AggregationsType } from 'src/app/models/api/aggregations.model';
import { IntegrationDataFlowModel } from 'src/app/models/api/integrationDataFlow.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IntegrationStatusToApiValue } from 'src/app/models/selectOptions/integrationStatusSelectOptions.model';
import { HttpService } from '../http/http.service';

@Injectable({
  providedIn: 'root',
})
export class AggregationsHttpServiceService {
  /**
   * The aggregations base url
   * @private
   * @readonly
   */
  private readonly _aggregationsBaseUrl: string = 'aggregations';

  constructor(private readonly _httpService: HttpService) {}

  /**
   * @public Get integrations
   * @returns an Observable{integrationModel} with the integrations returned from server
   */
  public getAggregations(
    aggregationsType: AggregationsType,
    filterOptions?: ISelectedFilters
  ): Observable<AggregationsModel> {
    const url = `${this._aggregationsBaseUrl}/${aggregationsType}`;
    const httpParams = this._createAggregationsQueryParams(filterOptions);

    return this._httpService.get<AggregationsModel>(url, undefined, httpParams);
  }

  /**
   * @public Get integrations' data flow aggregations
   * @returns an Observable{IntegrationDataFlowModel} with all the integrations data flow returned from server
   */
  public getIntegrationsDataflow(): Observable<IntegrationDataFlowModel> {
    const url = `${this._aggregationsBaseUrl}/integrations/dataflow`;
    return this._httpService.get<IntegrationDataFlowModel>(url);
  }

  /**
   * @public Get AIDR aggregations
   * @returns an Observable{AIDRAggregations} with the AIDR aggregations returned from server
   */
  public getAIDRAggregations(integrationId: string, periodInDays = 90): Observable<AIDRAggregations> {
    const url = `detection/integrations/${integrationId}/findings-by-rule-stat?periodInDays=${periodInDays}`;

    // Expect an array from the HTTP call, then transform it
    return this._httpService.get<{ key: string; count: number }[]>(url, undefined, undefined, true).pipe(
      map((responseArray) => {
        if (!Array.isArray(responseArray)) {
          // Handle unexpected response format, though httpService.get should ideally align with <T>
          console.error('AIDR Aggregations: Expected an array but received:', responseArray);
          return {} as AIDRAggregations; // Return empty or throw error
        }
        return responseArray.reduce((acc, item) => {
          acc[item.key] = item.count;
          return acc;
        }, {} as AIDRAggregations);
      }),
      catchError((error) => {
        if (error.status === HttpStatusCode.InternalServerError) {
          console.error('AIDR Aggregations: Internal Server Error occurred:', error);
          // Optionally, return a user-friendly error object or an empty object
          return of({} as AIDRAggregations);
        }
        // Re-throw other errors
        throw error;
      })
    );
  }

  private _createAggregationsQueryParams(filterOptions?: ISelectedFilters) {
    let httpParams = new HttpParams();
    if (filterOptions === undefined) return httpParams;

    if (filterOptions.integration && filterOptions.integration.length > 0) {
      httpParams = httpParams.append('integrationId', filterOptions.integration.join(','));
    }

    if (filterOptions.policy && filterOptions.policy.length > 0) {
      httpParams = httpParams.append('policyId', filterOptions.policy.join(','));
    }

    if (filterOptions.integrationStatus?.length) {
      filterOptions.integrationStatus.forEach((status) => {
        const apiValue = IntegrationStatusToApiValue[status] ?? status;
        httpParams = httpParams.append('integrationStatus', apiValue);
      });
    }

    return httpParams;
  }
}
